import { ProjectAction, ProjectSource, ProjectStatus } from 'generated/graphql';

export type TProjectStagesData = {
  hasAnyStage: boolean;
  numberOfStages: number;
};

export type TProjectContext = {
  stagesData: TProjectStagesData;
  projectStatus?: ProjectStatus;
  projectSource?: ProjectSource;
};

export type TValidationStrategy = (
  policyValue: boolean,
  context: TProjectContext,
) => boolean;

export type TActionConfig = {
  action: ProjectAction;
  validationStrategy?: TValidationStrategy;
};

export const isRoleKickoffProject = (
  projectSource?: ProjectSource,
  projectStatus?: ProjectStatus,
) => {
  return (
    projectSource === ProjectSource.RoleKickoff &&
    projectStatus === ProjectStatus.Draft
  );
};
/**
 * Computes project stages data from project query result
 */
export const computeProjectStagesData = (
  interviewStages?: Array<{ subId: string }> | null,
  assessmentStages?: Array<{ id: string }> | null,
  shortlistStages?: Array<{ subId: string }> | null,
): TProjectStagesData => {
  const hasInterviewStages = Boolean(interviewStages?.length);
  const hasAssessmentStages = Boolean(assessmentStages?.length);
  const hasShortlistStages = Boolean(shortlistStages?.length);
  return {
    hasAnyStage:
      hasInterviewStages || hasAssessmentStages || hasShortlistStages,
    numberOfStages:
      (interviewStages?.length ?? 0) +
      (assessmentStages?.length ?? 0) +
      (shortlistStages?.length ?? 0),
  };
};

/**
 * Validates a single action based on its configuration
 */
export const validateAction = (
  config: TActionConfig,
  policyValue: boolean,
  context: TProjectContext,
): boolean => {
  if (config.validationStrategy) {
    return config.validationStrategy(policyValue, context);
  }
  return policyValue;
};

/**
 * Creates a map of action to policy value from the backend response
 */
export const createPolicyValueMap = (
  policyActions: readonly boolean[] | undefined,
): Map<ProjectAction, boolean> => {
  const map = new Map<ProjectAction, boolean>();
  ACTION_CONFIGS.forEach((config, index) => {
    map.set(config.action, policyActions?.[index] ?? false);
  });
  return map;
};

/**
 * Registry of all project actions with their validation strategies.
 * Actions without a validationStrategy will use the backend policy value directly.
 * Any new actions should be added here
 */
export const ACTION_CONFIGS: readonly TActionConfig[] = [
  // Actions with custom frontend validation
  {
    action: ProjectAction.CreateProjectJobRole,
    validationStrategy: (policyValue, { stagesData }) =>
      policyValue && !stagesData.hasAnyStage,
  },
  {
    action: ProjectAction.RemoveProjectJobRole,
    validationStrategy: (
      policyValue,
      { stagesData, projectStatus, projectSource },
    ) => {
      if (isRoleKickoffProject(projectSource, projectStatus)) {
        return false;
      }
      return policyValue && !stagesData.hasAnyStage;
    },
  },
  {
    action: ProjectAction.RemoveAssessmentStage,
    validationStrategy: (policyValue, { stagesData, projectStatus }) =>
      projectStatus === ProjectStatus.Published
        ? stagesData.numberOfStages > 1
        : policyValue,
  },
  {
    action: ProjectAction.RemoveInterviewStage,
    validationStrategy: (policyValue, { stagesData, projectStatus }) =>
      projectStatus === ProjectStatus.Published
        ? stagesData.numberOfStages > 1
        : policyValue,
  },
  {
    action: ProjectAction.RemoveShortlistStage,
    validationStrategy: (policyValue, { stagesData, projectStatus }) =>
      projectStatus === ProjectStatus.Published
        ? stagesData.numberOfStages > 1
        : policyValue,
  },
  // Actions that only use BE policy validation
  {
    action: ProjectAction.CreateShortlistStage,
  },
  {
    action: ProjectAction.UpdateShortlistStageName,
  },
  {
    action: ProjectAction.CreateAssessmentStage,
    // Short-term guard: only allow adding an assessment stage while the
    // role is still DRAFT or REVIEW. Publishing (or closing) a role locks R4
    // category / trait-weight calculation, so adding a stage afterwards can make
    // assessments fail. Non-editable statuses are blocked outright on the frontend.
    validationStrategy: (policyValue, { projectStatus }) =>
      projectStatus === ProjectStatus.Draft ||
      projectStatus === ProjectStatus.Review
        ? policyValue
        : false,
  },
  {
    action: ProjectAction.UpdateAssessmentStageName,
  },
  {
    action: ProjectAction.AddAssessmentStageAction,
  },
  {
    action: ProjectAction.RemoveAssessmentStageAction,
  },
  {
    action: ProjectAction.CreateInterviewStage,
  },
  {
    action: ProjectAction.UpdateInterviewStageName,
  },
  {
    action: ProjectAction.UpdateProjectLanguages,
  },
  {
    action: ProjectAction.UpdateProjectIndirectInviteSsoSignUp,
  },
  {
    action: ProjectAction.UpdateProjectCobranding,
  },
] as const;

export const DEFAULT_ACTIONS = ACTION_CONFIGS.map((config) => config.action);

export type TProjectPolicyActions = Record<
  typeof DEFAULT_ACTIONS[number],
  boolean
>;
